import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, Mail, Sparkles, ClipboardPaste, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useFeatures, isFeatureVisible } from "@/lib/features";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const lbl = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#71717a]";
const inp = "h-9 border-white/10 bg-black/30 text-[13px] text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40";
const TONES = ["professional", "warm", "confident", "enthusiastic", "formal"];

export default function CoverLetter() {
  useFeatures();
  const [cvs, setCvs] = useState([]);
  const [cvId, setCvId] = useState("");
  const [personal, setPersonal] = useState({});
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [tone, setTone] = useState("professional");
  const [jd, setJd] = useState("");
  const [notes, setNotes] = useState("");
  const [letter, setLetter] = useState("");
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const loading = useRef(true);

  useEffect(() => {
    (async () => {
      try {
        const l = await api.cvsList();
        setCvs(l.cvs || []);
        if (l.cvs?.length) { setCvId(l.cvs[0].id); const g = await api.cvsGet(l.cvs[0].id); setPersonal(g.data?.personal || {}); }
      } catch { /* feature may be off */ } finally { loading.current = false; }
    })();
  }, []);

  const pickCv = async (id) => { setCvId(id); try { const g = await api.cvsGet(id); setPersonal(g.data?.personal || {}); } catch {} };

  const pasteJd = async () => {
    try { const t = await navigator.clipboard.readText(); if (t) { setJd((jd ? jd + "\n" : "") + t); toast.success("Pasted"); } }
    catch { toast.error("Clipboard blocked — paste with Ctrl/Cmd+V."); }
  };

  const generate = async () => {
    if (!cvId) return toast.error("Select a CV first.");
    setBusy(true);
    try { const r = await api.cvCoverLetter({ cv_id: cvId, job_description: jd, company, role, tone, notes }); setLetter(r.text || ""); toast.success("Cover letter ready — edit as you like"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Generation failed."); } finally { setBusy(false); }
  };

  const download = async () => {
    if (!letter.trim()) return;
    setDl(true);
    try {
      const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
      const res = await api.cvCoverLetterExport({ body: letter, personal, date_str: date });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(personal.full_name || "cover_letter").replace(/\s+/g, "_")}_cover_letter.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Download failed."); } finally { setDl(false); }
  };

  if (!isFeatureVisible("cover_letter_writer")) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white"><Navbar />
        <div className="mx-auto max-w-lg px-6 py-24 text-center" data-testid="cl-unavailable">
          <Mail className="mx-auto mb-4 text-white/30" size={40} />
          <h1 className="text-xl font-bold">Cover Letter Writer is currently unavailable</h1>
          <Link to="/tools" className="mt-6 inline-block rounded-full bg-white px-4 py-2 text-sm font-bold text-black">Back to Tools</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="mx-auto max-w-[1300px] px-4 py-6 sm:px-6">
        <div className="mb-5 flex items-center gap-3">
          <Link to="/tools" data-testid="cl-back" className="flex items-center gap-1 text-sm text-white/60 hover:text-white"><ArrowLeft size={16} /> Tools</Link>
          <h1 className="font-display text-xl font-black tracking-tight">Cover Letter Writer</h1>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* form */}
          <div className="space-y-3 rounded-xl border border-white/10 bg-[#0e0f11] p-4">
            <div>
              <label className={lbl}>Use details from CV</label>
              <Select value={cvId} onValueChange={pickCv}>
                <SelectTrigger className="h-9 border-white/10 bg-white/5 text-[13px] text-white" data-testid="cl-cv-select"><SelectValue placeholder={cvs.length ? "Select a CV" : "No CVs yet — create one first"} /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#111214] text-white">
                  {cvs.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`cl-cv-${c.id}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div><label className={lbl}>Company</label><Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="cl-company" className={inp} placeholder="e.g. TechCo" /></div>
              <div><label className={lbl}>Role</label><Input value={role} onChange={(e) => setRole(e.target.value)} data-testid="cl-role" className={inp} placeholder="e.g. Senior Engineer" /></div>
            </div>
            <div>
              <label className={lbl}>Tone</label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="h-9 border-white/10 bg-white/5 text-[13px] capitalize text-white" data-testid="cl-tone"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#111214] text-white">{TONES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between"><label className={lbl}>Job Description</label>
                <button onClick={pasteJd} data-testid="cl-paste-jd" className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300 hover:text-emerald-200"><ClipboardPaste size={12} /> Paste</button>
              </div>
              <Textarea value={jd} onChange={(e) => setJd(e.target.value)} rows={6} data-testid="cl-jd" placeholder="Paste the job description (optional but recommended)." className="border-white/10 bg-black/30 text-[13px] text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40" />
            </div>
            <div>
              <label className={lbl}>Notes to weave in (optional)</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="cl-notes" placeholder="Anything genuine you want emphasised — the AI won't invent facts." className="border-white/10 bg-black/30 text-[13px] text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40" />
            </div>
            <button onClick={generate} disabled={busy} data-testid="cl-generate" className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2.5 text-[13px] font-bold text-black hover:bg-emerald-400 disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate Cover Letter</button>
          </div>

          {/* result */}
          <div className="flex flex-col rounded-xl border border-white/10 bg-[#0e0f11] p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[13px] font-bold text-white">Your Cover Letter</span>
              <button onClick={download} disabled={!letter.trim() || dl} data-testid="cl-download" className="inline-flex items-center gap-1.5 rounded-md border border-white/20 px-3 py-1.5 text-[12px] font-bold text-white hover:border-white/40 disabled:opacity-40">{dl ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download PDF</button>
            </div>
            {letter ? (
              <Textarea value={letter} onChange={(e) => setLetter(e.target.value)} data-testid="cl-result" className="min-h-[520px] flex-1 border-white/10 bg-white text-[13.5px] leading-relaxed text-[#1a1a1a] focus-visible:ring-emerald-400/40" />
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/10 py-20 text-center text-white/40" data-testid="cl-empty">
                <FileText size={28} /><p className="text-sm">Fill in the details and generate your tailored cover letter.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
